import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS, API_CONFIG } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo, PaginatedResponse } from "./baseApi";
import { WorkOrderPriority } from "./workOrderApi";

// Enum for WIP Status (if needed)
export enum WIPStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  READY_FOR_PRODUCTION = 'READY_FOR_PRODUCTION',
  WAITING_FOR_MATERIALS = 'WAITING_FOR_MATERIALS',
  ON_HOLD = 'ON_HOLD',
  OVERDUE = 'OVERDUE'
}

// Main WIP Summary interface matching the Java DTO
export interface WorkInProgressSummary {
  // Core Work Order Information
  workOrderNumber: string;
  endgoodName: string;
  endgoodId: number; // Add endgood ID for variant lookup
  processName: string;
  processStepId: number;
  wipQuantity: number;
  nextProcessName: string;
  nextProcessStepId: number;
  salesOrderNumber: string;
  priority: WorkOrderPriority;
  
  // Additional useful information
  workOrderId: number;
  customerName: string;
  department: string;
  assignedTo: string;
  progressPercentage: number;
  isOverdue: boolean;
  hasMaterialIssued: boolean;
  isReadyForProduction: boolean;
  bomHeaderId?: number; // BOM header ID from API response
  nextProcessWorkAreaId?: number; // Work area ID for the next process
  productionDate?: string; // Production date for the work order
  issueQuantity?: number; // Issue quantity for the work order
}

export interface WIPResponse extends PaginatedResponse<WorkInProgressSummary> {}

// Filter interface for WIP queries
export interface WIPFilters {
  status?: WIPStatus;
  priority?: WorkOrderPriority;
  department?: string;
  assignedTo?: string;
  customerName?: string;
  startDate?: string;
  endDate?: string;
  isOverdue?: boolean;
  hasMaterialIssued?: boolean;
  isReadyForProduction?: boolean;
  minProgressPercentage?: number;
  maxProgressPercentage?: number;
}

// Filter request interface for POST /wip-summary/filter endpoint
export interface WIPFilterRequest {
  branchId?: number;
  endgoodId?: number;
  processId?: number;
  processStepId?: number;
  bomHeaderId?: number;
  salesOrderNumber?: string;
  workOrderNumber?: string;
}

// Statistics interface for WIP dashboard
export interface WIPStats {
  totalWIPTasks: number;
  overdueTasks: number;
  readyForProduction: number;
  withMaterialIssued: number;
  averageProgress: number;
  highPriorityTasks: number;
  mediumPriorityTasks: number;
  lowPriorityTasks: number;
  departmentBreakdown: { [department: string]: number };
  priorityBreakdown: { [priority: string]: number };
}

class WIPApiService extends BaseApiService {

  // Get all WIP tasks with pagination and filters
  async getWIPTasks(
    page: number = 1, 
    pageSize: number = 10,
    filters?: WIPFilters,
    branchId?: number
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to query parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      // Use branchId in the path if provided, otherwise use default or query param
      const endpoint = branchId 
        ? `${API_ENDPOINTS.WIP_TRACKING.LIST(branchId.toString())}?${params.toString()}`
        : `${API_ENDPOINTS.WIP_TRACKING.LIST('1')}?${params.toString()}`; // Default to branch 1 if not provided

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(endpoint);
      
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error fetching WIP tasks:', error);
      toast.error('Failed to fetch WIP tasks');
      throw error;
    }
  }

  // Get all WIP tasks without pagination (simple list)
  async getAllWIPTasks(filters?: WIPFilters, branchId?: number): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const finalBranchId = branchId || 1; // Default to branch 1 if not provided
      let endpoint = API_ENDPOINTS.WIP_TRACKING.LIST(finalBranchId.toString());
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      }

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(endpoint);
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error fetching all WIP tasks:', error);
      toast.error('Failed to fetch WIP tasks');
      throw error;
    }
  }

  // Filter WIP tasks using POST /wip-summary/filter endpoint
  async filterWIPTasks(filterRequest: WIPFilterRequest): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const endpoint = API_ENDPOINTS.WIP_TRACKING.FILTER;
      const requestBody = JSON.stringify(filterRequest);
      
      console.log('🌐 WIPApiService.filterWIPTasks - Making API call:', {
        endpoint,
        method: HTTP_METHODS.POST,
        requestBody,
        fullUrl: `${API_CONFIG.BASE_URL}${endpoint}`
      });
      
      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        endpoint,
        {
          method: HTTP_METHODS.POST,
          body: requestBody
        }
      );
      
      // console.log('✅ WIPApiService.filterWIPTasks - Response received:', response);
      return response;
    } catch (error) {
      // console.error('❌ WIPApiService: Error filtering WIP tasks:', error);
      toast.error('Failed to filter WIP tasks');
      throw error;
    }
  }

  // Search WIP tasks
  async searchWIPTasks(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: WIPFilters
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to search parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.SEARCH}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error searching WIP tasks:', error);
      toast.error('Failed to search WIP tasks');
      throw error;
    }
  }

  // Get WIP tasks by status
  async getWIPTasksByStatus(
    status: WIPStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.BY_STATUS(status)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error(`WIPApiService: Error fetching WIP tasks by status ${status}:`, error);
      toast.error(`Failed to fetch ${status.toLowerCase()} WIP tasks`);
      throw error;
    }
  }

  // Get WIP tasks by priority
  async getWIPTasksByPriority(
    priority: WorkOrderPriority, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.BY_PRIORITY(priority)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error(`WIPApiService: Error fetching WIP tasks by priority ${priority}:`, error);
      toast.error(`Failed to fetch ${priority.toLowerCase()} priority WIP tasks`);
      throw error;
    }
  }

  // Get WIP tasks by department
  async getWIPTasksByDepartment(
    department: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.BY_DEPARTMENT(department)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error(`WIPApiService: Error fetching WIP tasks by department ${department}:`, error);
      toast.error(`Failed to fetch WIP tasks for department ${department}`);
      throw error;
    }
  }

  // Get WIP tasks by assigned person
  async getWIPTasksByAssignedTo(
    assignedTo: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.BY_ASSIGNED_TO(assignedTo)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error(`WIPApiService: Error fetching WIP tasks assigned to ${assignedTo}:`, error);
      toast.error(`Failed to fetch WIP tasks assigned to ${assignedTo}`);
      throw error;
    }
  }

  // Get WIP tasks by customer
  async getWIPTasksByCustomer(
    customerName: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.BY_CUSTOMER(customerName)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error(`WIPApiService: Error fetching WIP tasks by customer ${customerName}:`, error);
      toast.error(`Failed to fetch WIP tasks for customer ${customerName}`);
      throw error;
    }
  }

  // Get WIP tasks by date range
  async getWIPTasksByDateRange(
    startDate: string,
    endDate: string,
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.BY_DATE_RANGE}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error fetching WIP tasks by date range:', error);
      toast.error('Failed to fetch WIP tasks for the specified date range');
      throw error;
    }
  }

  // Get overdue WIP tasks
  async getOverdueWIPTasks(
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.OVERDUE}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error fetching overdue WIP tasks:', error);
      toast.error('Failed to fetch overdue WIP tasks');
      throw error;
    }
  }

  // Get WIP tasks ready for production
  async getReadyForProductionTasks(
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.READY_FOR_PRODUCTION}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error fetching ready for production WIP tasks:', error);
      toast.error('Failed to fetch ready for production WIP tasks');
      throw error;
    }
  }

  // Get WIP tasks with material issued
  async getWIPTasksWithMaterialIssued(
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkInProgressSummary[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkInProgressSummary[]>>(
        `${API_ENDPOINTS.WIP_TRACKING.MATERIAL_ISSUED}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error fetching WIP tasks with material issued:', error);
      toast.error('Failed to fetch WIP tasks with material issued');
      throw error;
    }
  }

  // Get WIP statistics
  async getWIPStats(filters?: WIPFilters): Promise<ApiResponse<WIPStats>> {
    try {
      let endpoint = API_ENDPOINTS.WIP_TRACKING.STATS;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      }

      const response = await this.makeRequest<ApiResponse<WIPStats>>(endpoint);
      return response;
    } catch (error) {
      // console.error('WIPApiService: Error fetching WIP statistics:', error);
      toast.error('Failed to fetch WIP statistics');
      throw error;
    }
  }

  // Export WIP tasks
  async exportWIPTasks(
    filters?: WIPFilters, 
    format: 'csv' | 'excel' = 'csv'
  ): Promise<Blob> {
    try {
      let endpoint = API_ENDPOINTS.WIP_TRACKING.EXPORT;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        params.append('format', format);
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      } else {
        endpoint += `?format=${format}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1'}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success('WIP tasks exported successfully');
      return await response.blob();
    } catch (error) {
      // console.error('WIPApiService: Error exporting WIP tasks:', error);
      toast.error('Failed to export WIP tasks');
      throw error;
    }
  }

  // Helper method to extract data from API response
  extractData<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.message || 'API request failed');
    }
    return response.data;
  }

  // Helper method to get WIP tasks with extracted data
  async getWIPTasksData(
    page: number = 1, 
    pageSize: number = 10,
    filters?: WIPFilters
  ): Promise<{ wipTasks: WorkInProgressSummary[], pagination: PaginationInfo }> {
    const response = await this.getWIPTasks(page, pageSize, filters);
    const wipTasks = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: wipTasks.length,
      pageSize: pageSize
    };
    
    return {
      wipTasks,
      pagination
    };
  }

  // Helper method to search WIP tasks with extracted data
  async searchWIPTasksData(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: WIPFilters
  ): Promise<{ wipTasks: WorkInProgressSummary[], pagination: PaginationInfo }> {
    const response = await this.searchWIPTasks(query, page, pageSize, filters);
    const wipTasks = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: wipTasks.length,
      pageSize: pageSize
    };
    
    return {
      wipTasks,
      pagination
    };
  }
}

export const wipApiService = new WIPApiService();
